import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const AmenitiesSection: React.FC = () => {
  const { t } = useTranslation();
  const [openSection, setOpenSection] = useState<string | null>(null);



  const heroAmenities = [
    { key: 'amenities.hero.items.pool', icon: '🏊' },
    { key: 'amenities.hero.items.wifi', icon: '⚡' },
    { key: 'amenities.hero.items.ac', icon: '❄️' },
    { key: 'amenities.hero.items.views', icon: '🏔️' },
    { key: 'amenities.hero.items.parking', icon: '🚗' },
    { key: 'amenities.hero.items.outdoor', icon: '🍽️' },
  ];

  const detailedSections = [
    'kitchen',
    'family',
    'bedbath',
    'work',
    'outdoor',
    'special'
  ];

  return (
    <section id="amenities" className="py-24 px-6 section-gradient-subtle">
      <div className="container">
        <div className="section-card shadow-layered">
          <h2 className="heading-main text-center mb-10">
            {t('amenities.hero.title')}
          </h2>

          {/* Hero Grid - Always Visible */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {heroAmenities.map((item, index) => (
              <div
                key={index}
                className="feature-item flex-col items-center text-center p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="text-3xl mb-2" aria-hidden="true">{item.icon}</div>
                <p className="text-sm font-semibold text-gray-800 leading-tight m-0">
                  {t(item.key)}
                </p>
              </div>
            ))}
          </div>

          <div className="divider-geometric"></div>

          {/* Accordion Sections - Click to Open */}
          <div className="space-y-3">
            {detailedSections.map((section) => {
              const sectionKey = `amenities.detailed.${section}`;

              // Get title directly
              const title = t(`${sectionKey}.title`);

              // Get items object by using the translation function
              // We need to manually construct the object from individual keys
              const sampleItems: Record<string, string[]> = {
                kitchen: ['coffee', 'appliances', 'special', 'grill', 'baking', 'pantry', 'dining'],
                family: ['poolSafety', 'kidsPool', 'baby', 'safety', 'security', 'privacy', 'play'],
                bedbath: ['bedding', 'blinds', 'toiletries', 'bath'],
                work: ['workspace', 'tech', 'connectivity', 'lighting'],
                outdoor: ['relax', 'views', 'terrace', 'climate'],
                special: ['description', 'equipment', 'fairUse', 'guarantee']
              };

              const itemKeys = sampleItems[section] || [];

              return (
                <details
                  key={section}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                  open={openSection === section}
                  onChange={(e) => {
                    if (e.currentTarget.open) {
                      setOpenSection(section);
                    }
                  }}
                >
                  <summary className="cursor-pointer p-4 bg-warm-cream hover:bg-warm-cream/80 transition-colors flex items-center justify-between select-none">
                    <h3 className="text-lg font-bold flex items-center gap-2 m-0">
                      {section === 'kitchen' && '🍳'}
                      {section === 'family' && '👨‍👩‍👧‍👦'}
                      {section === 'bedbath' && '🛏️'}
                      {section === 'work' && '💻'}
                      {section === 'outdoor' && '🌞'}
                      {section === 'special' && '👨‍🍳'}
                      {title}
                    </h3>
                    <span className="text-2xl font-light">
                      {openSection === section ? '−' : '+'}
                    </span>
                  </summary>
                  <div className="p-4 bg-white border-t border-gray-200">
                    <ul className="space-y-2">
                      {itemKeys.map((itemKey: string) => {
                        const itemText = t(`${sectionKey}.items.${itemKey}`);
                        return (
                          <li key={itemKey} className="flex items-start gap-2">
                            <span className="text-green-600 mt-1 font-bold">✓</span>
                            <span
                              className="text-gray-700 text-sm leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: itemText }}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </details>
              );
            })}
          </div>

          {/* Important Note */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900 font-semibold text-center">
              ⚠️ {t('amenities.detailed.family.items.poolSafety')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AmenitiesSection;
